import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastMutation } from '../utils/mutation';
import { onAngularEvent } from '../../services/angular-bridge';
import { reportTaskComplete, reportTaskProgress } from '../../services/taskProgress';
import { notifySuccess } from '../../services/notify';

export interface SearchTermList {
  id: string;
  projectId: string;
  name: string;
  status: string;
  createdAt: string;
  tag: { id: string };
  duplicateTag: { id: string };
}

export interface SearchTermListsPage {
  searchTermLists: SearchTermList[];
}

export interface SearchTerm {
  id: string;
  projectId: string;
  searchTermListId: string;
  term: string;
  isValid: boolean;
  errorMessage?: string;
  tag?: { id: string };
}

export interface SearchTermsPage {
  searchTerms: SearchTerm[];
}

export interface SearchTermJob {
  id: string;
  createdAt: string;
  status: string;
  totalDocHits: number;
  uniqueParentHits: number;
  uniqueHits: number;
  uniqueHitsWithFamily: number;
  directDownloadPath?: string;
}

export interface SearchTermJobsPage {
  searchTermJobs: SearchTermJob[];
}

const listsKey = (projectId: string) => ['searchTermLists', projectId];
const termsKey = (projectId: string, listId: string) => ['searchTerms', projectId, listId];
const jobsKey = (projectId: string, listId: string) => ['searchTermJobs', projectId, listId];

export function useSearchTermLists(projectId: string) {
  return useQuery<SearchTermListsPage>({
    queryKey: listsKey(projectId),
    queryFn: () => api.get<SearchTermListsPage>(`/project/${projectId}/searchtermlist`),
    enabled: !!projectId,
  });
}

export function useCreateSearchTermList(projectId: string) {
  return useToastMutation(
    listsKey(projectId),
    ({ name, terms }: { name: string; terms?: string[] }) =>
      api.post<SearchTermList>(`/project/${projectId}/searchtermlist`, { projectId, name, terms }),
    {
      success: (_d, v) => ({ title: 'Search Term List added successfully', description: `Name: ${v.name}`, timeout: 5000 }),
    }
  );
}

export function useSearchTerms(projectId: string, listId: string) {
  return useQuery<SearchTermsPage>({
    queryKey: termsKey(projectId, listId),
    queryFn: () =>
      api.get<SearchTermsPage>(`/project/${projectId}/searchtermlist/${listId}/terms`),
    enabled: !!projectId && !!listId,
  });
}

export function useCreateSearchTerm(projectId: string, listId: string) {
  return useToastMutation(
    termsKey(projectId, listId),
    (term: string) =>
      api.post<SearchTerm>(`/project/${projectId}/searchtermlist/${listId}/terms`, {
        projectId,
        searchTermListId: listId,
        term,
      }),
    {
      success: () => ({ title: 'Search term added', description: 'The search term was added successfully', timeout: 5000 }),
    }
  );
}

export function useUpdateSearchTerm(projectId: string, listId: string) {
  return useToastMutation(
    termsKey(projectId, listId),
    (term: SearchTerm) =>
      api.put<SearchTerm>(
        `/project/${projectId}/searchtermlist/${listId}/terms/${term.id}`,
        term
      ),
    {
      success: () => ({ title: 'Search term updated', description: 'The search term was updated successfully', timeout: 5000 }),
    }
  );
}

export function useDeleteSearchTerm(projectId: string, listId: string) {
  return useToastMutation(
    termsKey(projectId, listId),
    (termId: string) =>
      api.delete(`/project/${projectId}/searchtermlist/${listId}/terms/${termId}`),
    {
      success: () => ({ title: 'Search term deleted', description: 'The search term was deleted successfully', timeout: 5000 }),
    }
  );
}

export function useSearchTermJobs(projectId: string, listId: string, isRunning: boolean) {
  return useQuery<SearchTermJobsPage>({
    queryKey: jobsKey(projectId, listId),
    queryFn: () =>
      api.get<SearchTermJobsPage>(`/project/${projectId}/searchtermlist/${listId}/job`),
    enabled: !!projectId && !!listId,
    refetchInterval: isRunning ? 5000 : false,
  });
}

export function useRunSearchTermJob(projectId: string, listId: string) {
  return useToastMutation(
    jobsKey(projectId, listId),
    () => api.post(`/project/${projectId}/searchtermlist/${listId}/job/run`),
    {
      success: () => ({ title: 'Search term list started', description: 'The search term list was started successfully', timeout: 5000 }),
    }
  );
}

export function useCancelSearchTermJob(projectId: string, listId: string) {
  // Errors surface via the global handler.
  return useToastMutation(
    jobsKey(projectId, listId),
    () => api.post(`/project/${projectId}/searchtermlist/${listId}/job/cancel`),
  );
}

/**
 * FeedService.SearchTermUpdate / SearchTermComplete payload. The job's own fields are the
 * useful ones — the top-level `name` comes through empty, so `description` is the label.
 * `id` is the job id (it matches `job.id`); the list is identified by `job.searchTermListId`.
 */
export interface SearchTermJobMessage {
  projectId: string;
  id: string;
  name?: string;
  description?: string;
  job?: {
    id: string;
    percentageComplete: number;
    status: string;
    description?: string;
    searchTermListId?: string;
    createdAt?: number;
    numQueued?: number;
    numProcessed?: number;
  };
}

/**
 * Every search-term job websocket event: the header task dropdown, the completion toast and
 * the list/job refresh. Mounted once by WebSocketBridge — a job outlives the Search Terms
 * tab, so these must keep working from anywhere in the app.
 *
 * No cache patching for progress: job rows carry no percentage field, and useSearchTermJobs
 * already polls every 5s while a job runs. Invalidating per frame would be a request per
 * websocket message.
 */
export function useSearchTermJobEvents() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const offUpdate = onAngularEvent('FeedService.SearchTermUpdate', (message: SearchTermJobMessage) => {
      if (!message?.job || !message.projectId) return;

      const label = message.description || message.job.description || message.name || 'Search Term Job';
      reportTaskProgress({
        id: message.id,
        name: label,
        description: label,
        createdAt: message.job.createdAt,
        percentageComplete: message.job.percentageComplete,
      });
    });

    const offComplete = onAngularEvent('FeedService.SearchTermComplete', (message: SearchTermJobMessage & { description: string }) => {
      notifySuccess({ title: 'Search Terms Job Complete', description: `${message.description} has completed.` });
      reportTaskComplete(message.id);

      if (!message.projectId) return;
      queryClient.invalidateQueries({ queryKey: listsKey(message.projectId) });
      // The list id is only on the job payload; without it, refresh every list's jobs.
      const listId = message.job?.searchTermListId;
      queryClient.invalidateQueries({
        queryKey: listId ? jobsKey(message.projectId, listId) : ['searchTermJobs', message.projectId],
      });
    });

    return () => { offUpdate(); offComplete(); };
  }, [queryClient]);
}
